<template>
  <div class="analyses-tab">
    <!-- Soft hint when multi-block mode is on. Analyses still run on
         the primary block only — multi-block recipes would need a job
         queue UI for the 30-540s × N latency, deferred for now. -->
    <div
      v-if="multiSelectActive && extraBlockCount > 0"
      class="alert alert-info small mb-2"
    >
      <i class="fas fa-info-circle"></i>
      Analyses run on the primary block only.
      <strong>{{ selectedBlock && selectedBlock.name }}</strong> will be used.
      The other {{ extraBlockCount }} selected block{{ extraBlockCount === 1 ? '' : 's' }}
      will be skipped here — switch to the Browse tab to save scenes across
      all selected blocks.
    </div>
    <!-- Recipe library -->
    <div v-if="!activeRecipe" class="recipe-library">
      <p class="text-muted small mb-3">
        <i class="fas fa-flask"></i>
        Run predefined analyses over a time range. Results land as standard
        datasets on your farm map.
      </p>

      <div class="recipe-grid">
        <a
          v-for="recipe in recipes"
          :key="recipe.id"
          class="recipe-card"
          :class="{ disabled: !canRun }"
          @click="canRun && selectRecipe(recipe)"
        >
          <div class="recipe-card-icon"><i :class="recipe.icon"></i></div>
          <div class="recipe-card-body">
            <div class="recipe-card-title">{{ recipe.label }}</div>
            <div class="recipe-card-desc">{{ recipe.description }}</div>
            <div class="recipe-card-tags">
              <span
                v-for="t in recipe.tags"
                :key="t"
                class="recipe-card-tag"
              >{{ t }}</span>
            </div>
          </div>
          <div class="recipe-card-chevron"><i class="fas fa-chevron-right"></i></div>
        </a>
      </div>

      <div v-if="!canRun" class="alert alert-info mt-3 small">
        Select a block to enable analyses.
      </div>
    </div>

    <!-- Recipe form -->
    <div v-else class="recipe-form">
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary mb-3 back-btn"
        @click="cancelRecipe"
      >
        <i class="fas fa-chevron-left"></i> Back to analyses
      </button>

      <h6 class="mb-1"><i :class="activeRecipe.icon"></i> {{ activeRecipe.label }}</h6>
      <p class="text-muted small mb-3">{{ activeRecipe.description }}</p>

      <!-- Date range -->
      <div class="form-group">
        <label>Date range</label>
        <div class="d-flex gap-2">
          <input
            type="date"
            v-model="form.startDate"
            class="form-control form-control-sm"
          />
          <input
            type="date"
            v-model="form.endDate"
            class="form-control form-control-sm"
          />
        </div>
        <small class="form-text text-muted">
          Typical: 2–4 month growing window.
        </small>
      </div>

      <!-- Source -->
      <div class="form-group">
        <label>Source</label>
        <select v-model="form.source" class="form-control form-control-sm">
          <option value="s2-ndvi">🛰️ Sentinel-2 NDVI (10 m, vegetation vigor)</option>
          <option value="s1-vh">📡 Sentinel-1 VH backscatter in dB (10 m, cloud-free moisture / structure)</option>
        </select>
      </div>

      <!-- Aggregation -->
      <div class="form-group">
        <label>Aggregation</label>
        <select v-model="form.aggregation" class="form-control form-control-sm">
          <option value="mean">Mean — average value per cell</option>
          <option value="median">Median — middle value (robust to outliers)</option>
          <option value="max">Max — peak value</option>
          <option value="min">Min — trough value</option>
          <option value="stddev">Standard deviation — variability per cell</option>
          <option value="count">Count — number of valid observations per cell</option>
        </select>
      </div>

      <!-- Block-level cloud filtering (optical only). Replaces the old
           "max cloud cover %" slider, which was a scene-wide aggregate
           and didn't catch the case of a 5% cloudy scene where the
           clouds happened to sit over your block. The recipe now
           samples each candidate scene's SCL classification clipped to
           the block area and excludes any scene where >5% of the block
           pixels are cloudy / cloud-shadow / cirrus. No knob — the
           filter just does the right thing automatically. -->
      <div v-if="form.source === 's2-ndvi'" class="cloud-filter-notice">
        <i class="fas fa-cloud-sun"></i>
        <div>
          <strong>Block-level cloud filtering</strong>
          <small class="d-block text-muted">
            Each candidate scene is checked against the actual block area
            (using Sentinel-2's SCL classification, not the scene-wide
            cloud percentage). Cloudy-over-block scenes are excluded
            automatically.
          </small>
        </div>
      </div>

      <!-- Run button -->
      <b-btn
        v-if="!running"
        variant="primary"
        block
        :disabled="!canSubmit"
        @click="runRecipe"
      >
        <i class="fas fa-play"></i> Run analysis
      </b-btn>

      <small v-if="!running" class="form-text text-muted mt-2">
        This typically takes 20–90 seconds depending on date range and source.
      </small>

    </div>

    <!-- Full-screen progress modal while running, then through success
         or failure until the user dismisses. Visually matches the
         save-progress overlay in SatellitePanel.vue so satellite runs
         and analysis runs have the same modal experience. -->
    <div v-if="progressState" class="analysis-progress-overlay">
      <div class="analysis-progress-card">
        <div class="analysis-progress-header">
          <div class="analysis-progress-title">
            <i v-if="progressState === 'running'" class="fas fa-flask"></i>
            <i v-else-if="progressState === 'complete'" class="fas fa-check-circle text-success"></i>
            <i v-else-if="progressState === 'failed'" class="fas fa-exclamation-triangle text-danger"></i>
            <span>
              <template v-if="progressState === 'running'">{{ progressTitleRunning }}</template>
              <template v-else-if="progressState === 'complete'">Analysis complete</template>
              <template v-else-if="progressState === 'failed'">Analysis failed</template>
            </span>
          </div>
          <div class="analysis-progress-sub">
            <template v-if="progressState === 'running'">
              {{ progressPercent }}% · {{ progressCurrentStageLabel }}
            </template>
            <template v-else-if="progressState === 'complete'">
              Dataset is ready in your datasets drawer.
            </template>
            <template v-else-if="progressState === 'failed'">
              {{ progressError || 'Something went wrong while running the analysis.' }}
            </template>
          </div>
        </div>

        <!-- Progress bar — fills to 100% on success, kept visible (in
             red) on failure so the user sees how far it got. -->
        <div class="analysis-progress-bar-outer">
          <div
            class="analysis-progress-bar-inner"
            :class="{ 'is-failed': progressState === 'failed' }"
            :style="{ width: progressBarWidth + '%' }"
          ></div>
        </div>

        <!-- Live detail line — shows the recipe's most recent server
             message ("12 of 18 scenes downloaded (7 valid, 5 skipped
             (cloudy over blocks))"). -->
        <div v-if="progress && progress.detail" class="analysis-progress-detail">
          {{ progress.detail }}
        </div>

        <!-- Ordered stage timeline. Past stages show check; current
             shows spinner; future show empty circle. -->
        <ol class="analysis-stages">
          <li
            v-for="stage in stageList"
            :key="stage.id"
            :class="'stage-' + stageStatus(stage.id)"
          >
            <span class="stage-icon">
              <i v-if="stageStatus(stage.id) === 'done'" class="fas fa-check"></i>
              <i v-else-if="stageStatus(stage.id) === 'active'" class="fas fa-spinner fa-spin"></i>
              <i v-else-if="stageStatus(stage.id) === 'failed'" class="fas fa-times"></i>
              <i v-else class="far fa-circle"></i>
            </span>
            <span class="stage-name">{{ stage.label }}</span>
          </li>
        </ol>

        <!-- Footer — only visible after the run resolves. Mirrors the
             save-progress footer pattern: one big "Done" button that
             dismisses, plus a contextual secondary action on failure. -->
        <div v-if="progressState !== 'running'" class="analysis-progress-footer">
          <b-btn
            v-if="progressState === 'failed'"
            variant="outline-secondary"
            class="mr-2"
            @click="dismissProgress"
          >
            Close
          </b-btn>
          <b-btn
            v-if="progressState === 'failed'"
            variant="primary"
            @click="retryAnalysis"
          >
            <i class="fas fa-redo"></i> Try again
          </b-btn>
          <b-btn
            v-if="progressState === 'complete'"
            variant="primary"
            block
            @click="dismissProgress"
          >
            <i class="fas fa-check"></i> Done
          </b-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-console */

const fb = require('@/firebaseConfig.js')

const ENDPOINT = 'https://us-central1-efficient-vineyard.cloudfunctions.net/runAnalysisRecipe'

// The canonical ordered list of stages. Used to render the timeline and to
// map the current backend stage to a position in the UI.
const STAGES = [
  { id: 'queued', label: 'Queued' },
  { id: 'building', label: 'Building analysis grid' },
  { id: 'search', label: 'Searching satellite archive' },
  { id: 'fetching', label: 'Fetching scene imagery' },
  { id: 'aggregating', label: 'Computing aggregation' },
  { id: 'saving', label: 'Saving dataset' },
  { id: 'complete', label: 'Complete' }
]

export default {
  name: 'AnalysesTab',
  props: {
    selectedBlock: {
      type: Object,
      default: null
    },
    // Surfaced from the parent when the satellite drawer is in
    // multi-select mode. Used purely to show a notice — the recipes
    // themselves still run against `selectedBlock` only for now.
    // Multi-block analyses would need a job-queue UI to handle the
    // 30-540s × N latency cleanly, so they're a separate task.
    multiSelectActive: {
      type: Boolean,
      default: false
    },
    extraBlockCount: {
      type: Number,
      default: 0
    }
  },
  data () {
    const end = new Date()
    const start = new Date(Date.now() - 120 * 24 * 60 * 60 * 1000)
    return {
      recipes: [
        {
          id: 'temporal_mean',
          label: 'Temporal Aggregate',
          icon: 'fas fa-chart-area',
          description: 'Aggregate (mean / median / max / min / stddev / count) a satellite product over any date range.',
          tags: ['researcher', 'Sentinel-2 NDVI', 'Sentinel-1 VH']
        }
      ],
      activeRecipe: null,
      running: false,
      // Progress modal state machine. null = no modal. 'running' = while
      // the recipe is in flight. 'complete' = stays open until the user
      // dismisses so they can see what landed. 'failed' = same, plus the
      // error message + a retry button. The modal styling matches the
      // save-progress overlay in SatellitePanel.vue so analysis runs and
      // satellite save runs feel like the same UX.
      progressState: null,
      progressError: null,
      // Snapshot of the form payload at run-time so "Try again" can
      // re-submit without forcing the user to retype.
      lastRunPayload: null,
      jobId: null,
      progress: null,
      progressUnsubscribe: null,
      form: {
        startDate: start.toISOString().slice(0, 10),
        endDate: end.toISOString().slice(0, 10),
        source: 's2-ndvi',
        aggregation: 'mean',
        // Generous coarse pre-filter to cut wasted STAC calls; the
        // recipe's per-block SCL check (5% over the block area) does
        // the precision filtering server-side, so this only needs to
        // exclude obviously hopeless scenes.
        cloudCoverMax: 70
      }
    }
  },
  computed: {
    canRun () {
      return !!(this.selectedBlock && this.selectedBlock.id)
    },
    canSubmit () {
      if (!this.canRun) return false
      if (!this.form.startDate || !this.form.endDate) return false
      return new Date(this.form.startDate) < new Date(this.form.endDate)
    },
    stageList () {
      // Hide "queued" as its own line — it's briefly shown while waiting
      // for the first real update to arrive.
      return STAGES.filter(s => s.id !== 'queued')
    },
    currentStageIndex () {
      if (!this.progress || !this.progress.stage) return -1
      return STAGES.findIndex(s => s.id === this.progress.stage)
    },
    progressPercent () {
      if (!this.progress) return 0
      return Math.max(0, Math.min(100, Math.round(this.progress.percent || 0)))
    },
    /**
     * Width to render the progress bar at. While running, follows
     * percent. On success snaps to 100 so the bar shows complete even
     * if the last progress event didn't quite hit 100. On failure
     * keeps the last percent so the user sees how far the run got.
     */
    progressBarWidth () {
      if (this.progressState === 'complete') return 100
      return this.progressPercent
    },
    progressCurrentStageLabel () {
      if (!this.progress) return 'Starting…'
      return this.progress.stageLabel || 'Working…'
    },
    /**
     * Headline shown above the progress bar while running. Uses the
     * active recipe's label so the modal feels like a card about the
     * specific work in flight rather than a generic "Running…".
     */
    progressTitleRunning () {
      if (this.activeRecipe && this.activeRecipe.label) {
        return `Running ${this.activeRecipe.label}…`
      }
      return 'Running analysis…'
    }
  },
  methods: {
    selectRecipe (recipe) {
      this.activeRecipe = recipe
    },
    cancelRecipe () {
      this.activeRecipe = null
    },
    /**
     * Status of an individual stage in the timeline:
     *   done    – the backend has moved past it
     *   active  – currently executing
     *   failed  – stage where the run errored out (only when
     *             progressState === 'failed')
     *   pending – not yet reached
     */
    stageStatus (stageId) {
      const cur = this.currentStageIndex
      const idx = STAGES.findIndex(s => s.id === stageId)
      // On a successful complete, mark every stage as done so the
      // checkmark column reads as a finished pipeline.
      if (this.progressState === 'complete') return 'done'
      if (cur < 0) return 'pending'
      if (idx < cur) return 'done'
      if (idx === cur) {
        // The stage we were in when the run blew up is marked failed
        // rather than active so the user can see where it stopped.
        return this.progressState === 'failed' ? 'failed' : 'active'
      }
      return 'pending'
    },

    subscribeToJob (jobId) {
      this.teardownSubscription()
      const ref = fb.db.collection('analysisJobs').doc(jobId)
      this.progressUnsubscribe = ref.onSnapshot(snap => {
        if (snap.exists) {
          this.progress = snap.data()
        }
      }, err => {
        console.warn('Progress snapshot error:', err)
      })
    },

    teardownSubscription () {
      if (this.progressUnsubscribe) {
        try { this.progressUnsubscribe() } catch (e) { /* ignore */ }
        this.progressUnsubscribe = null
      }
    },

    async runRecipe () {
      if (!this.canSubmit) return

      // Mint a client-side job id and subscribe BEFORE firing the POST, so
      // we don't miss the first progress updates the function emits.
      const jobId = 'job_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8)
      this.jobId = jobId
      this.progress = { stage: 'queued', stageLabel: 'Starting analysis', percent: 0 }
      this.progressError = null
      this.progressState = 'running'
      this.subscribeToJob(jobId)

      this.running = true
      this.$emit('analysisStart', { recipe: this.activeRecipe })

      const body = {
        recipeId: this.activeRecipe.id,
        jobId,
        inputs: {
          dateRange: { start: this.form.startDate, end: this.form.endDate },
          source: this.form.source,
          aggregation: this.form.aggregation,
          cloudCoverMax: this.form.cloudCoverMax
        },
        blockId: this.selectedBlock.id,
        farmId: this.$store.state.farm.id,
        userId: this.$store.state.currentUser.uid
      }
      // Snapshot the payload so retryAnalysis can re-fire without
      // forcing the user back through the form.
      this.lastRunPayload = body
      try {
        // The recipe endpoint now requires a verified Firebase ID
        // token so it can attribute the credit charge and createdBy
        // to the actual signed-in user rather than trusting `userId`
        // in the body.
        const idToken = await fb.auth.currentUser.getIdToken()
        const resp = await fetch(ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          body: JSON.stringify(body)
        })
        const data = await resp.json().catch(() => ({}))
        if (!resp.ok || !data.success) {
          throw new Error(data.error || `HTTP ${resp.status}`)
        }
        this.$emit('analysisComplete', data)
        // Hold the modal open in "complete" state so the user can see
        // the final stage list + dataset is ready message until they
        // hit Done. activeRecipe is cleared on dismiss, not here.
        this.progressState = 'complete'
      } catch (err) {
        console.error('Analysis failed:', err)
        this.$emit('analysisError', err)
        this.progressError = err && err.message ? err.message : String(err)
        this.progressState = 'failed'
      } finally {
        this.running = false
        this.teardownSubscription()
        // Modal stays visible — no setTimeout teardown. The user closes
        // it explicitly via dismissProgress (Done / Close button).
      }
    },
    /**
     * Dismiss the progress modal and return to the recipe library.
     * Called from the Done button on success and the Close button on
     * failure. Clearing activeRecipe takes the user back to the
     * library view (the form is gone) which matches the save-progress
     * post-completion behavior in SatellitePanel.
     */
    dismissProgress () {
      this.progressState = null
      this.progress = null
      this.progressError = null
      this.jobId = null
      this.activeRecipe = null
    },
    /**
     * Re-fire the same recipe payload after a failure. Doesn't touch
     * the form values so the user can also tweak and click "Run again"
     * if they prefer.
     */
    async retryAnalysis () {
      if (!this.lastRunPayload || !this.activeRecipe) {
        this.dismissProgress()
        return
      }
      // Reset transient progress state but keep activeRecipe so the
      // form is still there if the user goes back.
      this.progressState = null
      this.progress = null
      this.progressError = null
      this.jobId = null
      // Defer one tick so the modal removal commits cleanly before
      // runRecipe re-opens it.
      this.$nextTick(() => this.runRecipe())
    }
  },
  beforeDestroy () {
    this.teardownSubscription()
  }
}
</script>

<style scoped>
.analyses-tab { padding: 0.25rem 0; }
.recipe-grid { display: flex; flex-direction: column; gap: 10px; }
.recipe-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #e5e9f0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.12s;
  text-decoration: none;
  color: inherit;
}
.recipe-card:hover {
  border-color: #0b5ed7;
  box-shadow: 0 2px 8px rgba(11,94,215,0.12);
  background: #f8fbff;
}
.recipe-card.disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.recipe-card.disabled:hover {
  border-color: #e5e9f0;
  box-shadow: none;
  background: #fff;
}
.recipe-card-icon {
  font-size: 1.4rem;
  color: #0b5ed7;
  flex-shrink: 0;
  width: 32px;
  text-align: center;
}
.recipe-card-body { flex: 1; }
.recipe-card-title { font-weight: 600; font-size: 0.95rem; }
.recipe-card-desc { font-size: 0.82rem; color: #495057; line-height: 1.35; margin-top: 2px; }
.recipe-card-tags { display: flex; gap: 4px; margin-top: 6px; flex-wrap: wrap; }
.recipe-card-tag {
  font-size: 0.68rem;
  background: #eef1f4;
  color: #495057;
  padding: 2px 8px;
  border-radius: 10px;
}
.recipe-card-chevron { color: #adb5bd; }

.recipe-form .form-group { margin-bottom: 0.85rem; }
.recipe-form label { font-size: 0.82rem; font-weight: 600; color: #495057; margin-bottom: 4px; }
.recipe-form .form-text { font-size: 0.74rem; }
.recipe-form .gap-2 { display: flex; gap: 8px; }
.recipe-form .gap-2 > * { flex: 1; }

/* Back button */
.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  padding: 4px 10px;
}

/* Cloud cover slider */
.cloud-slider-group .cloud-slider-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.cloud-slider-value {
  font-size: 0.8rem;
  color: #0b5ed7;
  font-weight: 600;
  background: #e9f3fe;
  padding: 2px 9px;
  border-radius: 10px;
}
.cloud-slider {
  width: 100%;
  height: 22px;
  appearance: none;
  -webkit-appearance: none;
  background: transparent;
  outline: none;
  margin: 0;
  padding: 0;
}
.cloud-slider::-webkit-slider-runnable-track {
  height: 6px;
  background: linear-gradient(90deg, #0b5ed7, #e9ecef);
  border-radius: 3px;
}
.cloud-slider::-moz-range-track {
  height: 6px;
  background: linear-gradient(90deg, #0b5ed7, #e9ecef);
  border-radius: 3px;
}
.cloud-slider::-webkit-slider-thumb {
  appearance: none;
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  background: #fff;
  border: 2px solid #0b5ed7;
  border-radius: 50%;
  cursor: pointer;
  margin-top: -5px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.15);
}
.cloud-slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  background: #fff;
  border: 2px solid #0b5ed7;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0,0,0,0.15);
}

/* Live progress panel */
/* Analysis-run progress modal — visually matches the save-progress
   overlay in SatellitePanel.vue (full-screen scrim + centered card +
   shared progress-bar treatment) so analysis and satellite-save flows
   read as the same UX language. */
.analysis-progress-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(3px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.analysis-progress-card {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
  width: 100%;
  max-width: 520px;
  padding: 22px 24px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
}
.analysis-progress-header {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 12px;
}
.analysis-progress-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: #212529;
  display: flex;
  align-items: center;
  gap: 8px;
}
.analysis-progress-title i { color: #0b5ed7; }
.analysis-progress-title .text-success { color: #198754 !important; }
.analysis-progress-title .text-danger { color: #dc3545 !important; }
.analysis-progress-sub { font-size: 0.85rem; color: #6c757d; }
.analysis-progress-bar-outer {
  width: 100%;
  height: 8px;
  background: #e9ecef;
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 14px;
}
.analysis-progress-bar-inner {
  height: 100%;
  background: linear-gradient(90deg, #0b5ed7, #3a8ef7);
  border-radius: 10px;
  transition: width 0.3s ease;
}
.analysis-progress-bar-inner.is-failed {
  background: linear-gradient(90deg, #b02a37, #dc3545);
}
.analysis-progress-detail {
  font-size: 0.78rem;
  color: #495057;
  margin-bottom: 10px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
  background: #fafbfc;
  padding: 6px 9px;
  border-radius: 4px;
  border: 1px solid #e5e9f0;
  word-break: break-word;
}
.analysis-stages {
  list-style: none;
  padding: 6px 8px;
  margin: 0;
  background: #fafbfc;
  border: 1px solid #e5e9f0;
  border-radius: 6px;
  overflow-y: auto;
  flex: 1;
}
.analysis-stages li {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 5px 4px;
  font-size: 0.83rem;
  color: #6c757d;
  transition: all 0.2s;
}
.analysis-stages li + li { border-top: 1px solid #eef1f4; }
.analysis-stages li.stage-done { color: #198754; }
.analysis-stages li.stage-done .stage-icon { color: #198754; }
.analysis-stages li.stage-active { color: #0b5ed7; font-weight: 600; }
.analysis-stages li.stage-active .stage-icon { color: #0b5ed7; }
.analysis-stages li.stage-failed { color: #dc3545; font-weight: 600; }
.analysis-stages li.stage-failed .stage-icon { color: #dc3545; }
.analysis-stages li.stage-pending .stage-icon { color: #adb5bd; }
.stage-icon { width: 18px; text-align: center; flex-shrink: 0; }
.analysis-progress-footer {
  margin-top: 14px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}
.analysis-progress-footer .btn-block { width: 100%; }
.text-success { color: #198754; }
.text-danger { color: #dc3545; }
</style>
